import { useState, useRef } from 'react'
import { Camera, Upload, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getAuthToken } from '../../services/api'

/**
 * Photo Upload Button Component
 * Integrates with device camera for mobile photo capture
 */
function PhotoUploadButton({ projectId, onUploadSuccess }) {
  const { t } = useTranslation()
  const [preview, setPreview] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef(null)
  const cameraInputRef = useRef(null)

  const handleFileSelect = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setPreview(reader.result)
    }
    reader.readAsDataURL(file)

    // Upload file
    await uploadPhoto(file)
  }

  const handleCameraCapture = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setPreview(reader.result)
    }
    reader.readAsDataURL(file)

    // Upload file
    await uploadPhoto(file)
  }

  const uploadPhoto = async (file) => {
    setIsUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('project_id', projectId)
      formData.append('photo_type', 'field_photo')

      const response = await fetch('/api/v1/documents/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken() || ''}`
        },
        body: formData
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Upload mislukt' }))
        const errorMessage = errorData.detail || `Upload mislukt (${response.status})`
        throw new Error(errorMessage)
      }

      const data = await response.json()
      setPreview(null)
      
      if (onUploadSuccess) {
        onUploadSuccess(data)
      }
    } catch (error) {
      console.error('Error uploading photo:', error)
      const errorMessage = error.message || t('mobile.photo.upload_error', { defaultValue: 'Fout bij uploaden foto' })
      alert(errorMessage)
    } finally {
      setIsUploading(false)
    }
  }

  const openCamera = () => {
    if (cameraInputRef.current) {
      cameraInputRef.current.click()
    }
  }

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  return (
    <div className="space-y-2">
      {/* Preview */}
      {preview && (
        <div className="relative">
          <img
            src={preview}
            alt="Preview"
            className="w-full h-48 object-cover rounded-lg"
          />
          <button
            onClick={() => setPreview(null)}
            className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full hover:bg-red-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2">
        {/* Camera Button (Mobile) */}
        <button
          onClick={openCamera}
          disabled={isUploading}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          <Camera className="w-5 h-5" />
          <span>{t('mobile.photo.take_photo', { defaultValue: 'Maak Foto' })}</span>
        </button>

        {/* File Upload Button */}
        <button
          onClick={openFilePicker}
          disabled={isUploading}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          <Upload className="w-5 h-5" />
          <span>{t('mobile.photo.upload', { defaultValue: 'Upload' })}</span>
        </button>
      </div>

      {/* Hidden Inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCameraCapture}
        className="hidden"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {isUploading && (
        <div className="text-center text-sm text-gray-600 dark:text-gray-400">
          {t('mobile.photo.uploading', { defaultValue: 'Uploaden...' })}
        </div>
      )}
    </div>
  )
}

export default PhotoUploadButton
